import {CommonModule} from '@angular/common';
import {IBaseComponent} from '../ibase/ibase.component';
import {PrimeNgImportsModule} from '../../prime-ng-imports';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {JSONSchema7} from 'json-schema';
import {isArray} from 'lodash';
import {JsonSchemaFormComponent} from './json-schema-form.component';
import {StringUtils} from '../../utils/string.util';
import {DEFAULT_TAB_NAME, JsonSchemaFormProperty, JsonSchemaFormUtils} from './json-schema-form-utils';

@Component({
    selector: 'app-json-schema-form-property',
    imports: [CommonModule, PrimeNgImportsModule],
    templateUrl: './json-schema-form-property.component.html',
    styles: `
        :host ::ng-deep p-datepicker {
            width: 100% !important;
        }
    `
})
export class JsonSchemaFormPropertyComponent extends IBaseComponent {
    protected readonly _booleanOptions = [
        {
            label: 'false / disable',
            value: false
        },
        {
            label: 'true / enable',
            value: true
        }
    ];

    protected _id!: string;
    protected _label!: string;
    protected _isNulled = false;
    protected _isNulledInv = false; /* Inverse state of isNulled, allow binding in a [(ngModel)] attribute */
    protected _activeTab = DEFAULT_TAB_NAME;
    protected _isRequiredComputed = false;
    protected _secretHasInitialValue = false;
    protected _secretUnlocked = false;
    private _patternRegex?: RegExp;
    private _patternRegexInvalid = false;
    private _validityPropagationQueued = false;
    private _queuedValidityPropagationValue: boolean | undefined;

    // ===============================================================
    // ===============================================================

    @Input()
    public jsonSchemaForm!: JsonSchemaFormComponent;

    @Input()
    public path!: string;
    @Input()
    public name!: string;
    @Input()
    public details!: JsonSchemaFormProperty;

    @Input()
    public target!: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    @Input()
    public targetKey!: string | number;
    @Input()
    public parentKeys!: string | number;

    @Output()
    public validityChangedEvent = new EventEmitter<boolean | undefined>();

    // ===============================================================
    // ===============================================================

    /**
     * Whether this secret field currently displays a masked placeholder instead of its real value.
     * True when the property is marked secret, it originally held a value when the form was loaded
     * (snapshotted once in `onReady()`) and has not been explicitly unlocked for edition by the user.
     * Based on the original value rather than the live one, so typing a fresh value into a field that
     * started empty (e.g. creation mode) never flips it back into masked mode.
     */
    protected get _isSecretMasked(): boolean {
        return this._secretHasInitialValue && !this._secretUnlocked;
    }

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this._id = this.parentKeys + '[' + this.targetKey + ']';
        this._label = StringUtils.toHumanFormatedStr(this.name);

        if (!this.details) throw new Error(`Schema is undefined` + this.path);

        if (this.details.isSecret) {
            const initialValue = this.target?.[this.targetKey];
            this._secretHasInitialValue = initialValue !== undefined && initialValue !== null && initialValue !== '';
        }

        this.handleDefaultValue();
        this.handleNulled();

        // Convert string to dates
        if (
            this.details.initialType == 'string' &&
            this.details.schema.format == 'date' &&
            this.target[this.targetKey] &&
            typeof this.target[this.targetKey] == 'string'
        ) {
            this.target[this.targetKey] = new Date(this.target[this.targetKey] as string);
        }

        this.preparePatternRegex();
        this.updateValidity();

        if (this.details.sub) {
            this._activeTab = this.details.sub.tabs[0];
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        delete this.jsonSchemaForm.context.validationErrors[this.path];
    }

    // ===============================================================
    // ===============================================================

    /**
     * Handle the potential default value of the property
     */
    private handleDefaultValue() {
        if (this.target[this.targetKey] !== null && this.target[this.targetKey] !== undefined) {
            return;
        }

        const schema = this.details.schema;
        if (schema.default !== undefined && schema.default !== null) {
            this.target[this.targetKey] = this.cloneDefaultValue(schema.default);
            return;
        }

        if (!this.details.required && this.details.nullable) {
            return;
        }

        if (this.details.initialType == 'object') {
            this.target[this.targetKey] = {};
            return;
        }

        if (this.details.initialType == 'array') {
            this.target[this.targetKey] = [];
        }
    }

    /**
     * Clone schema default value to avoid shared references.
     */
    private cloneDefaultValue(defaultValue: JSONSchema7['default']) {
        if (typeof defaultValue === 'object') {
            return structuredClone(defaultValue);
        }

        return defaultValue;
    }
    /**
     * Handle the "nulled" aspect of the property
     */
    private handleNulled() {
        if (this.details.nullable)
            this._isNulled = this.target[this.targetKey] === null || this.target[this.targetKey] === undefined;
        else {
            this._isNulled = false;
        }

        this._isNulledInv = !this._isNulled;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Compute required state according to nullable and null toggle.
     */
    private computeRequiredState(): void {
        if (this.details.nullable) {
            this._isRequiredComputed = !this._isNulled;
        } else {
            this._isRequiredComputed = this.details.required;
        }
    }

    /**
     * Validate current value and return validity state with optional message.
     */
    private evaluateCurrentValidity(): {isValid: boolean; error: string} {
        if (this._isSecretMasked) {
            return {isValid: true, error: ''};
        }

        const currentValue = this.target[this.targetKey];

        if (currentValue === undefined || currentValue === null) {
            if (this._isRequiredComputed) {
                return {isValid: false, error: 'Missing value'};
            }

            return {isValid: true, error: ''};
        }

        if (this.details.initialType !== 'string') {
            return {isValid: true, error: ''};
        }

        return this.evaluateStringValidity(String(currentValue));
    }

    /**
     * Evaluate the validity of a string value according to the schema pattern.
     * @param value Value to evaluate
     * @returns Validity state and optional error message
     */
    private evaluateStringValidity(value: string): {isValid: boolean; error: string} {
        if (value.length == 0) {
            return {isValid: false, error: 'Missing value'};
        }

        const schema = this.details.schema;
        if (schema.minLength !== undefined && value.length < schema.minLength) {
            return {isValid: false, error: `Value must be at least ${schema.minLength} characters long`};
        }

        if (schema.maxLength !== undefined && value.length > schema.maxLength) {
            return {isValid: false, error: `Value must be at most ${schema.maxLength} characters long`};
        }

        if (!schema.pattern) {
            return {isValid: true, error: ''};
        }

        if (this._patternRegexInvalid || !this._patternRegex) {
            return {isValid: false, error: 'Invalid format'};
        }

        this._patternRegex.lastIndex = 0;
        if (!this._patternRegex.test(value)) {
            return {isValid: false, error: 'Invalid format'};
        }

        return {isValid: true, error: ''};
    }

    /**
     * Prepare reusable regex validation once per component instance.
     */
    private preparePatternRegex(): void {
        this._patternRegex = undefined;
        this._patternRegexInvalid = false;

        const schema = this.details.schema;
        if (!schema.pattern) {
            return;
        }

        try {
            this._patternRegex = new RegExp(schema.pattern);
        } catch {
            this._patternRegexInvalid = true;
        }
    }

    /**
     * Apply computed validity state to shared error store and emit updates.
     */
    private applyValidityResult(isValid: boolean, error: string, forcePropagate?: boolean): void {
        const ctx = this.jsonSchemaForm.context;

        if (isValid && ctx.validationErrors[this.path]) {
            delete ctx.validationErrors[this.path];
            this.validityChangedEvent.emit();
            return;
        }

        if (!isValid) {
            const builtMessage = `${this.path}: ${error}`;
            if (ctx.validationErrors[this.path] != builtMessage) {
                ctx.validationErrors[this.path] = builtMessage;
                this.validityChangedEvent.emit();
            }

            return;
        }

        if (forcePropagate === true) {
            this.validityChangedEvent.emit(forcePropagate);
        }
    }

    /**
     * Update the validity of local properties
     * @param forcePropagate Force the signaling to the json schema form
     */
    protected updateValidity(forcePropagate?: boolean): void {
        this.computeRequiredState();
        JsonSchemaFormUtils.refreshContextRowReferencesOptions(this.jsonSchemaForm.context);

        const validityResult = this.evaluateCurrentValidity();
        this.applyValidityResult(validityResult.isValid, validityResult.error, forcePropagate);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Unlock a masked secret field so the user can overwrite its value.
     * Clears the current (masked) value, requiring the user to type a brand new one.
     */
    protected unlockSecret(): void {
        this._secretUnlocked = true;
        this.target[this.targetKey] = '';

        this.updateValidity();
    }

    /**
     * Coalesce child validity propagation so large nested structs do not bubble one event per field
     * during a single change detection turn.
     */
    protected queueValidityPropagation(value?: boolean): void {
        this._queuedValidityPropagationValue = value;

        if (this._validityPropagationQueued) {
            return;
        }

        this._validityPropagationQueued = true;
        queueMicrotask(() => {
            this._validityPropagationQueued = false;
            this.validityChangedEvent.next(this._queuedValidityPropagationValue);
            this._queuedValidityPropagationValue = undefined;
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Toggle nullable input
     */
    protected toggleNullable() {
        this._isNulled = !this._isNulledInv;

        if (this._isNulled) {
            // Use explicit null for nullable fields so submit payload can represent
            // an intentional clear operation instead of "missing/unchanged" semantics.
            this.target[this.targetKey] = null;
        } else {
            if (this.details.schema.default !== undefined && this.details.schema.default !== null) {
                this.target[this.targetKey] = this.details.schema.default;
            } else if (this.details.initialType == 'object') {
                this.target[this.targetKey] = {};
            } else if (this.details.initialType == 'array') {
                this.target[this.targetKey] = [];
            }
        }

        this.updateValidity();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Add an element to the array
     */
    protected addArrayElement() {
        if (!this.target[this.targetKey] || !isArray(this.target[this.targetKey])) {
            this.target[this.targetKey] = [];
        }

        // Make new array each time for angular is not lost
        if (this.details.arrayItemType?.type == 'object')
            this.target[this.targetKey] = [...this.target[this.targetKey], {}];
        else this.target[this.targetKey] = [...this.target[this.targetKey], null];

        JsonSchemaFormUtils.refreshContextRowReferencesOptions(this.jsonSchemaForm.context);
        this.updateValidity();
    }
    /**
     * Remove an element from the array
     * @param index Index of the element
     */
    protected delArrayElement(index: number) {
        this.globalsService.confirmationService.confirm({
            key: 'deletion-confirm',
            header: 'Deletion confirmation',
            message: `Are you sure that you want to delete element ${index + 1}'?`,
            accept: () => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const asArray = this.target[this.targetKey] as any[];
                asArray.splice(index, 1);

                // Make new array each time for angular is not lost
                this.target[this.targetKey] = [...this.target[this.targetKey]];

                // Delete any pending validation error
                delete this.jsonSchemaForm.context.validationErrors[`${this.path}[${index}]`];

                JsonSchemaFormUtils.refreshContextRowReferencesOptions(this.jsonSchemaForm.context);
                this.updateValidity(true);
            }
        });
    }
}
